import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { INTERNAL_EMAIL_ON_JOB_ROLES } from '@/constants/on-job-roles';
import { useGetOrgRolesQuery } from '@/store/query/roles';
import { useGetHiringTeamOfJob } from '@/api/useGetHiringTeamOfJob/useGetHiringTeamOfJob';
import { orgRoleToken } from '@/utils/internalEmailRecipients';
import {
  fetchUserSearchPage,
  searchedUserName,
  USER_SEARCH_PAGE_SIZE,
} from '@/api/userSearch/fetchUserSearch';

export interface RecipientOption {
  /** The token written to the form — a user id, a job role, or `org_role:<id>`. */
  value: string;
  label: string;
  /** Users only: shown under the name. */
  email?: string;
  /**
   * The roles this person holds, as the row prints them under the name in place
   * of an address — "Hiring manager, Recruiter, Coordinator". Only the resolved
   * Job roles section sets it; the hiring team sends no addresses, so the roles
   * are what that line has to say, and there is room to name them all.
   */
  roleLine?: string;
  image?: string;
  /**
   * Users only: every role they hold on this job, by bare name — e.g.
   * `["Hiring manager", "Recruiter"]`. Someone can hold more than one, so this
   * is a list rather than the single role it used to be: the row names the
   * first and counts the rest. "on job" is added by whichever label needs it,
   * not carried here.
   */
  tags?: string[];
  /** Roles only: the initials chip drawn in place of an avatar. */
  initials?: string;
  /** Roles nobody holds: selectable would mean addressing an empty set. */
  disabled?: boolean;
  /** Why the row is disabled, shown where a user row shows its tag. */
  hint?: string;
}

export interface RecipientSection {
  id: 'job_roles' | 'kula_roles' | 'users';
  title: string;
  /**
   * Whole-category total — not the number of rows rendered. Left off where the
   * design asks for no badge at all, which is the resolved Job roles section:
   * its rows are people, and a count of them says less than the names do.
   */
  count?: number;
  options: RecipientOption[];
  /**
   * Rows are on their way — the users section, which costs a round trip on open
   * and on every search. `options` may still hold the previous answer meanwhile,
   * so the menu says so rather than showing that answer as this one, or an
   * empty section as "No matches".
   */
  isLoading?: boolean;
  /** More rows are a page away — the users section, which the menu pages through. */
  hasMore?: boolean;
}

const initialsOf = (label: string) =>
  label
    .replace(/\(on [^)]*\)/i, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

/**
 * Roles whose stored name does not read as its own label. `job_sourcer` is the
 * only one today: the backend calls it a sourcer and the whole product calls it
 * an external recruiter, so deriving the name from the column would read
 * "Sourcer" to someone who has never seen it.
 */
const ROLE_NAME_OVERRIDES: Record<string, string> = {
  job_sourcer: 'External recruiter',
};

const EMPTY_JOB_ROLE_HINT = 'No one on this job';

/**
 * `"job_hiring_manager"` -> `"Hiring manager"`.
 *
 * The bare name, without "on job". That suffix says where the role applies, so
 * it belongs to the one label that has to stand alone — repeated down a list of
 * four it is the same three words four times over, and the names are what the
 * reader is actually comparing.
 */
const onJobRoleName = (roleName: string) => {
  const override = ROLE_NAME_OVERRIDES[roleName];
  if (override) return override;

  const words = roleName.replace(/^job_/, '').replace(/_/g, ' ').trim();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

/**
 * How a row names the roles its user holds: the first, and a count of the rest.
 *
 * "on job" lands here and nowhere else. This is the label that stands alone, so
 * it has to say where the role applies; the hover behind it lists bare names,
 * where the suffix would be the same three words on every line.
 */
export const onJobTagSummary = (tags: string[] = []): string | undefined => {
  if (tags.length === 0) return undefined;

  const first = `${tags[0]} on job`;
  return tags.length === 1 ? first : `${first} & +${tags.length - 1}`;
};

/**
 * The roles a chip names, in brackets after the person: "Primary hiring manager
 * & +1".
 *
 * Without "on job" — a chip sits in the To row of an email about a candidate on
 * a job, so where the role applies is the one thing the surrounding screen has
 * already said. `onJobTagSummary` is the badge's form, which does have to say
 * it, and puts the suffix inside rather than after the count.
 */
export const roleBracketLabel = (tags: string[] = []): string | undefined => {
  if (tags.length === 0) return undefined;
  return tags.length === 1 ? tags[0] : `${tags[0]} & +${tags.length - 1}`;
};

export const recipientUserSearchKey = (query: string) =>
  ['internal-email-recipient-users', query] as const;

/**
 * The three sections behind the recipient picker.
 *
 * Job and Kula roles are small, fixed lists, so they arrive whole and filter in
 * memory. Users cannot: an account has thousands, so the section opens on the
 * first page and defers to the same server-side search the Cc/Bcc fields use
 * (ENG-3766). The badge reads `users_counts_by_status.active`, which the search
 * response carries — paging could never report the account total.
 */
export const useRecipientSections = (jobId?: string) => {
  const { data: orgRolesData } = useGetOrgRolesQuery({});
  const { data: hiringTeam } = useGetHiringTeamOfJob(String(jobId ?? ''), {
    enabled: Boolean(jobId),
  });

  /**
   * userId -> every role they hold on this job.
   *
   * Someone can be both a hiring manager and a recruiter, and the row used to
   * keep only whichever came first — so a user who was on the job twice over
   * read as if they held one role. Kept in the order the hiring team lists its
   * roles, which is the order that already decided the single tag, so the role
   * named first is the one that was named before.
   */
  const tagsByUserId = useMemo(() => {
    const map = new Map<string, string[]>();
    (hiringTeam?.roles ?? []).forEach((role) => {
      const label = onJobRoleName(role.name);
      (role.users ?? []).forEach((user) => {
        const key = String((user as { id?: unknown })?.id ?? '');
        if (!key) return;
        const held = map.get(key);
        if (!held) {
          map.set(key, [label]);
          // Two roles can share a name — `job_sourcer` is overridden to read
          // "External recruiter" — and the same role listing a user twice would
          // otherwise be counted twice.
        } else if (!held.includes(label)) {
          held.push(label);
        }
      });
    });
    return map;
  }, [hiringTeam]);

  /**
   * The Job roles section once a job is known: the people on it, a row per role
   * they hold.
   *
   * A role token means "whoever holds this when the email goes out", which is
   * the right answer for a template in Settings and an unhelpfully abstract one
   * on a real job, where the sender wants to see the names they are addressing.
   * Here the role is resolved to its people up front, and the row says which
   * role put them there.
   *
   * One row per person, not per role. Someone on the job twice over is one
   * recipient and reads as one — their roles are counted on the line under the
   * name, the way the Users section counts them in its badge.
   */
  const jobRoleUserOptions = useMemo<RecipientOption[]>(() => {
    const byUser = new Map<string, RecipientOption>();

    (hiringTeam?.roles ?? []).forEach((role) => {
      (role.users ?? []).forEach((user) => {
        const id = String(user?.id ?? '');
        if (!id || byUser.has(id)) return;

        byUser.set(id, {
          value: id,
          // `ats_hiring_teams` serialises its people through
          // `api/v1/users/card`, which is `{id, name, avatar}` — not the
          // `first_name`/`last_name`/`email` the user SEARCH returns. Reading
          // them with the search's name helper found none of those fields and
          // fell through to its last resort, so every row read "User 347".
          label: user?.name?.trim() || `User ${id}`,
          image: user?.avatar ?? undefined,
          tags: tagsByUserId.get(id),
        });
      });
    });

    // Built last, off the finished tag list, so the line names every role the
    // person holds rather than the ones seen by the time their row was made.
    //
    // Named, not counted: the line has the row to itself — no address competes
    // for it — so "& +2" would hide what there is room to say. The badge in the
    // Users section counts instead, because there it shares a row with a name
    // and an address.
    return Array.from(byUser.values()).map((option) => ({
      ...option,
      roleLine: (option.tags ?? []).join(', ') || undefined,
    }));
  }, [hiringTeam, tagsByUserId]);

  const memberCountByRole = useMemo(() => {
    const counts = new Map<string, number>();
    (hiringTeam?.roles ?? []).forEach((role) => {
      counts.set(role.name, (role.users ?? []).length);
    });
    return counts;
  }, [hiringTeam]);

  /**
   * The list is a fixed constant, so every job role is offered on every job —
   * but a role nobody holds resolves to nobody at send time, which surfaced as
   * `err_internal_email_no_recipients` only after the composer was submitted.
   *
   * Disabled, not hidden: the role still exists, and saying so is more useful
   * than a menu that changes shape per job. Only a loaded hiring team can prove
   * a role is empty — while it is missing, every role stays selectable rather
   * than the whole section greying out.
   */
  const jobRoleOptions = useMemo<RecipientOption[]>(
    () =>
      INTERNAL_EMAIL_ON_JOB_ROLES.map((role) => {
        const isEmpty =
          Boolean(hiringTeam) && (memberCountByRole.get(role.value) ?? 0) === 0;

        return {
          value: role.value,
          label: role.label,
          initials: initialsOf(role.label),
          disabled: isEmpty,
          ...(isEmpty ? { hint: EMPTY_JOB_ROLE_HINT } : {}),
        };
      }),
    [hiringTeam, memberCountByRole]
  );

  const orgRoleOptions = useMemo<RecipientOption[]>(() => {
    const roles =
      (
        orgRolesData as {
          org_roles?: Array<{
            id: number;
            name: string;
            members_count?: number;
          }>;
        }
      )?.org_roles ?? [];
    return (
      roles
        // Left out entirely, not greyed out. A Kula role nobody holds resolves to
        // nobody, and the account has enough of them that a row per empty one is
        // a list of things that cannot be picked (CORE-5384). A job role is kept
        // and disabled instead: that list is six fixed rows, and which of them
        // this job has nobody for is worth saying.
        //
        // `members_count` comes off the org_roles payload. Absent (an older
        // response) is unknown, not empty — only an explicit zero drops a role.
        .filter((role) => role.members_count !== 0)
        .map((role) => ({
          value: orgRoleToken(role.id),
          label: role.name,
          initials: initialsOf(role.name),
        }))
    );
  }, [orgRolesData]);

  return { jobRoleOptions, jobRoleUserOptions, orgRoleOptions, tagsByUserId };
};

/**
 * Matches the debounce kula's own async Select applies to `loadOptions`, which
 * is what the Cc/Bcc user search runs on.
 */
const USER_SEARCH_DEBOUNCE_MS = 300;

/** Users for the picker: first page on open, server-side search on type. */
export const useRecipientUsers = (
  query: string,
  tagsByUserId: Map<string, string[]>
) => {
  const typed = query.trim();

  // The two role sections filter in memory and stay instant; this one costs a
  // round trip, so it waits for a pause. Without it every keystroke fires its
  // own request and the list trails the input by a character or more.
  const [term, setTerm] = useState(typed);
  useEffect(() => {
    if (typed === term) return undefined;
    const id = setTimeout(() => setTerm(typed), USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [typed, term]);
  // Paged, not one fixed page: the section's badge reads the account total, so a
  // single page of 50 left "Users (115)" over 50 rows with no way to reach the
  // rest. The menu asks for the next page as it scrolls.
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: recipientUserSearchKey(term),
      queryFn: ({ pageParam, signal }) =>
        fetchUserSearchPage({
          query: term,
          items: USER_SEARCH_PAGE_SIZE,
          page: pageParam,
          signal,
        }),
      initialPageParam: 1,
      // A short page is the last one. The endpoint reports no page count, and a
      // full page with nothing behind it costs one empty request, once.
      getNextPageParam: (last, pages) =>
        last.users.length < USER_SEARCH_PAGE_SIZE
          ? undefined
          : pages.length + 1,
      staleTime: 60_000,
      placeholderData: (previous) => previous,
    });

  const users = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.users),
    [data]
  );

  const options = useMemo<RecipientOption[]>(
    () =>
      users.map((user) => ({
        value: String(user.id),
        label: searchedUserName(user),
        email: user.email ?? undefined,
        image: user.avatar_thumb_url ?? undefined,
        tags: tagsByUserId.get(String(user.id)),
      })),
    [users, tagsByUserId]
  );

  // Asked for as the list nears its end. Guarded here rather than at the call
  // site: the menu fires this on every scroll event.
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    options,
    // Whole-account total while unfiltered; the match count once searching.
    count: term
      ? options.length
      : (data?.pages?.[0]?.counts?.active ?? options.length),
    // Also true while the debounce is still pending, so a caller showing a
    // spinner does not blink idle between the keystroke and the request.
    isFetching: isFetching || typed !== term,
    hasMore: Boolean(hasNextPage),
    loadMore,
  };
};

/** Case-insensitive label match, for the two in-memory sections. */
export const filterByLabel = (options: RecipientOption[], query: string) => {
  const term = query.trim().toLowerCase();
  if (!term) return options;
  return options.filter((option) => option.label.toLowerCase().includes(term));
};
